import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RelatedVaccinesPanel from "../components/research/RelatedVaccinesPanel";
import ScoreMeter from "../components/ScoreMeter";
import SourceItem from "../components/SourceItem";
import Timeline from "../components/Timeline";
import { TagBadgeRow } from "../components/TagFilters";
import SEO, { topicJsonLd, breadcrumbJsonLd } from "../components/SEO";
import { trackTopicView, installScrollDepthTracker, trackPathwayClick } from "../lib/analytics";
import ShareDock from "../components/ShareDock";
import EntityCard from "../components/EntityCard";
import EvidenceItem from "../components/EvidenceItem";
import { PremiumSection, ContinueInvestigationCTA } from "../components/PremiumLock";
import { GhostEvidenceCard, GhostTimelineRow, GhostSourceCard } from "../components/GhostCards";
import NextInvestigations from "../components/NextInvestigations";
import ShareAttribution from "../components/ShareAttribution";
import SourceLeadCTA from "../components/SourceLeadCTA";
import {
    HelpCircle, ArrowRight, CheckCircle2, AlertCircle,
    BookOpenCheck, FileWarning, Scale, ChevronLeft, Flag,
} from "lucide-react";

function Section({ id, label, title, children }) {
    return (
        <section id={id} data-testid={`section-${id}`} className="py-10 sm:py-14 border-t border-[#243044]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-3">
                {label}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-6">
                {title}
            </h2>
            <div className="text-[15px] leading-[1.75] text-[#CBD5E1]">
                {children}
            </div>
        </section>
    );
}

function EvidenceTier({ tier, items, ghosts, color, icon: Icon, sources }) {
    if ((!items || items.length === 0) && (!ghosts || ghosts.length === 0)) return null;
    const totalCount = (items?.length || 0) + (ghosts?.length || 0);
    return (
        <div className={`bg-[#111827] border border-[#243044] rounded-xl overflow-hidden`}>
            <div className={`flex items-center gap-2 px-5 py-3 border-b border-[#243044] ${color}`}>
                <Icon className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold">{tier}</span>
                <span className="text-[10px] text-[#64748B] ml-auto">{totalCount} item{totalCount === 1 ? "" : "s"}</span>
            </div>
            <div className="p-5 space-y-5 divide-y divide-[#243044]">
                {(items || []).map((item, i) => (
                    <div key={`f-${i}`} className={i > 0 ? "pt-5" : ""}>
                        <EvidenceItem item={item} sources={sources} />
                    </div>
                ))}
                {ghosts && ghosts.length > 0 && (
                    <div className={`${(items?.length || 0) > 0 ? "pt-5" : ""} space-y-3`}>
                        {ghosts.map((g, i) => (
                            <GhostEvidenceCard key={`g-${i}`} item={g} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function TopicPage() {
    const { slug } = useParams();
    const [topic, setTopic] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/topics/${slug}`)
            .then((r) => setTopic(r.data))
            .catch((e) => setError(e?.response?.status === 404 ? "Topic not found" : "Failed to load topic"))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (topic?.slug) trackTopicView(topic.slug, { category: topic.category });
    }, [topic?.slug, topic?.category]);

    // Build 3 — scroll-depth events (25/50/75/90). One-shot per threshold.
    useEffect(() => {
        if (!topic?.slug) return undefined;
        const teardown = installScrollDepthTracker({
            slug: topic.slug,
            category: topic.category,
        });
        return teardown;
    }, [topic?.slug, topic?.category]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0B1220]">
                <Header />
                <div className="max-w-5xl mx-auto px-4 py-32 text-center text-sm text-[#64748B]">Loading topic…</div>
                <Footer />
            </div>
        );
    }

    if (error || !topic) {
        return (
            <div className="min-h-screen bg-[#0B1220]">
                <Header />
                <div className="max-w-5xl mx-auto px-4 py-32 text-center">
                    <h1 className="text-3xl font-bold text-white mb-3">{error || "Topic not found"}</h1>
                    <Link to="/" className="text-sm text-[#56CCF2] hover:text-white">← Back to library</Link>
                </div>
                <Footer />
            </div>
        );
    }

    const s = topic.sections || {};
    const hero = s.hero || {};
    const why = s.why_this_matters || {};
    const official = s.official_position || {};
    const breakdown = s.system_breakdown || {};
    const questions = s.critical_questions || [];
    const evidence = s.evidence_sections || {};
    const supporting = s.supporting_view || {};
    const critical = s.critical_view || {};
    const examples = s.real_world_examples || [];
    const unknowns = s.unknowns || [];
    const future = s.future_outlook || {};
    const takeaways = s.key_takeaways || [];
    const next = s.next_topics || [];

    // Progressive disclosure: gate metadata from backend.
    const gate = topic._gate || { is_member: true };
    const isMember = !!gate.is_member;
    const counts = gate.counts || {};
    const evGhosts = gate.ghosts?.evidence || {};
    const tlGhosts = gate.ghosts?.timeline || [];
    const srcGhosts = gate.ghosts?.sources || [];

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <SEO
                title={topic.title}
                description={topic.subtitle || topic.summary}
                canonicalPath={`/topic/${topic.slug}`}
                image={topic.featured_image}
                type="article"
                jsonLd={[
                    topicJsonLd(topic),
                    breadcrumbJsonLd([
                        { name: "Library", path: "/" },
                        ...(topic.category ? [{ name: topic.category, path: `/category/${encodeURIComponent(topic.category)}` }] : []),
                        { name: topic.title },
                    ]),
                ]}
            />
            <ShareDock
                title={topic.title}
                description={topic.subtitle || topic.summary}
                type="topic"
                id={topic.slug}
            />
            <Header />

            {/* Breadcrumb */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <Link to="/" className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#56CCF2] uppercase tracking-wider">
                    <ChevronLeft className="w-3 h-3" />
                    Back to library
                </Link>
            </div>

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
                <div className="fade-in">
                    {topic.category && (
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-4 inline-flex items-center gap-3 flex-wrap">
                            <span>{topic.category}</span>
                            {topic.topic_layer === "foundational" ? (
                                <span
                                    data-testid="topic-hero-foundational-badge"
                                    className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-[#56CCF2]/10 border border-[#56CCF2]/30 text-[#56CCF2] font-medium"
                                >
                                    Foundational system
                                </span>
                            ) : null}
                            {topic.status === "under_investigation" ? (
                                <span
                                    data-testid="topic-hero-under-investigation"
                                    className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-[#E0B341]/10 border border-[#E0B341]/30 text-[#E0B341] font-medium inline-flex items-center gap-1.5"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#E0B341] animate-pulse" />
                                    Under investigation
                                </span>
                            ) : null}
                            {topic.parent_topic_slug && topic.topic_layer === "sub_investigation" ? (
                                <Link
                                    to={`/topic/${topic.parent_topic_slug}`}
                                    data-testid="topic-hero-parent-link"
                                    className="inline-flex items-center gap-1 text-[#94A3B8] hover:text-[#56CCF2] transition-colors normal-case tracking-normal text-[11px]"
                                >
                                    <span className="opacity-60">Part of</span>
                                    <span className="underline-offset-2 hover:underline">
                                        {topic.parent_topic_slug.replace(/-/g, " ")}
                                    </span>
                                </Link>
                            ) : null}
                        </div>
                    )}
                    <h1 data-testid="topic-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6 max-w-4xl">
                        {hero.title || topic.title}
                    </h1>
                    {(hero.subtitle || topic.subtitle) && (
                        <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed max-w-3xl mb-6">
                            {hero.subtitle || topic.subtitle}
                        </p>
                    )}

                    {((topic.geography && topic.geography.length) || topic.intensity) ? (
                        <div className="mb-10" data-testid="topic-hero-tags">
                            <TagBadgeRow geography={topic.geography} intensity={topic.intensity} compact={false} />
                        </div>
                    ) : null}

                    {topic.featured_image && (
                        <div className="relative rounded-2xl overflow-hidden border border-[#243044] mb-10 aspect-[16/8] bg-[#111827]" data-testid="topic-featured-image">
                            <img
                                src={topic.featured_image.startsWith("http") ? topic.featured_image : `${BACKEND_URL}${topic.featured_image}`}
                                alt={topic.title}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-transparent to-transparent opacity-60" />
                        </div>
                    )}

                    {hero.hooks && hero.hooks.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
                            {hero.hooks.map((h, i) => (
                                <div key={i} className="px-4 py-3 rounded-xl bg-[#111827] border border-[#243044] text-sm text-[#CBD5E1] leading-snug">
                                    <span className="text-[#56CCF2] font-bold mr-2">·</span>
                                    {h}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Score meters */}
                    <div className="bg-[#111827] border border-[#243044] rounded-2xl p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="score-meters">
                        <ScoreMeter
                            label="Confidence Score"
                            value={topic.confidence_score}
                            kind="confidence"
                            description="How well-supported the documented evidence is across credible, independent sources."
                        />
                        <ScoreMeter
                            label="Concern Level"
                            value={topic.concern_level}
                            kind="concern"
                            description="How much real-world impact and unresolved risk this topic carries today."
                        />
                    </div>

                    <div className="mt-6 text-xs text-[#64748B] flex flex-wrap items-center gap-x-4 gap-y-2">
                        <span>{topic.compiled_by || "Compiled by Bill Mathers"}</span>
                        {topic.evidence_level && <span>· Evidence level: <span className="text-[#94A3B8] uppercase">{topic.evidence_level}</span></span>}
                        {topic.last_updated && <span>· Updated {new Date(topic.last_updated).toLocaleDateString()}</span>}
                    </div>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {/* 2. Why This Matters */}
                {(why.content || why.who_affected || why.why_now) && (
                    <Section id="why-this-matters" label="02 / Why this matters" title="Why this matters">
                        {why.content && <p className="mb-4 max-w-3xl">{why.content}</p>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                            {why.who_affected && (
                                <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                                    <div className="text-[10px] uppercase tracking-wider text-[#56CCF2] mb-2">Who is affected</div>
                                    <p className="text-sm text-[#CBD5E1]">{why.who_affected}</p>
                                </div>
                            )}
                            {why.why_now && (
                                <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                                    <div className="text-[10px] uppercase tracking-wider text-[#56CCF2] mb-2">Why now</div>
                                    <p className="text-sm text-[#CBD5E1]">{why.why_now}</p>
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                {/* 3. Official Position */}
                {official.content && (
                    <Section id="official-position" label="03 / Official position" title={official.label || "Official Position"}>
                        <div className="bg-[#111827] border-l-4 border-[#2F80ED] rounded-r-xl p-6 max-w-3xl">
                            <p className="text-[#CBD5E1]">{official.content}</p>
                        </div>
                    </Section>
                )}

                {/* 4. System Breakdown */}
                {(breakdown.who_builds || breakdown.who_funds || breakdown.who_benefits || breakdown.how_it_works) && (
                    <Section id="system-breakdown" label="04 / System breakdown" title="System breakdown">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                                ["Who builds", breakdown.who_builds],
                                ["Who funds", breakdown.who_funds],
                                ["Who benefits", breakdown.who_benefits],
                                ["How it works", breakdown.how_it_works],
                            ].filter(([, v]) => v).map(([label, value], i) => (
                                <div key={i} className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                                    <div className="text-[10px] uppercase tracking-wider text-[#56CCF2] mb-2">{label}</div>
                                    <p className="text-sm text-[#CBD5E1] leading-relaxed">{value}</p>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* 5. Critical Questions */}
                {questions.length > 0 && (
                    <Section id="critical-questions" label="05 / Critical questions" title="Critical questions">
                        <p className="text-sm text-[#94A3B8] mb-6 max-w-3xl">Open questions, not claims. Bring your own evidence.</p>
                        <ul className="bg-[#111827] border border-[#243044] rounded-xl divide-y divide-[#243044]">
                            {questions.map((q, i) => {
                                const text = typeof q === "string" ? q : q.question;
                                const context = typeof q !== "string" ? q.context : null;
                                return (
                                    <li key={i} data-testid={`critical-question-${i}`} className="px-5 py-4">
                                        <div className="flex items-start gap-3">
                                            <span className="text-[10px] font-mono text-[#64748B] mt-1 min-w-[20px]">Q{i + 1}</span>
                                            <HelpCircle className="w-4 h-4 mt-0.5 text-[#56CCF2] shrink-0" />
                                            <div className="flex-1">
                                                <p className="text-sm text-[#CBD5E1] leading-relaxed">{text}</p>
                                                {context && (
                                                    <p className="text-xs text-[#94A3B8] leading-relaxed mt-2">{context}</p>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </Section>
                )}

                {/* 6. Evidence & Data */}
                {(evidence.documented?.length || evidence.reported?.length || evidence.debated?.length
                    || evGhosts.documented?.length || evGhosts.reported?.length || evGhosts.debated?.length) > 0 && (
                    <Section id="evidence" label="06 / Evidence & data" title="Evidence and data">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <EvidenceTier tier="Documented / Verified" items={evidence.documented} ghosts={evGhosts.documented} color="text-[#56CCF2]" icon={BookOpenCheck} sources={topic.sources} />
                            <EvidenceTier tier="Reported / Emerging" items={evidence.reported} ghosts={evGhosts.reported} color="text-[#2F80ED]" icon={FileWarning} sources={topic.sources} />
                            <EvidenceTier tier="Debated / Uncertain" items={evidence.debated} ghosts={evGhosts.debated} color="text-[#94A3B8]" icon={Scale} sources={topic.sources} />
                        </div>
                        {!isMember && (evGhosts.documented?.length || evGhosts.reported?.length || evGhosts.debated?.length) > 0 && (
                            <div className="mt-8">
                                <ContinueInvestigationCTA
                                    compact
                                    testId="evidence-premium-cta"
                                    label="Full evidence depth"
                                    subline="Documented, reported and debated evidence — with source citations, strength labels, and attached documents — is reserved for members."
                                />
                            </div>
                        )}
                    </Section>
                )}

                {/* 7. Both Sides */}
                {(supporting.content || critical.content) && (
                    <Section id="both-sides" label="07 / Both sides" title="Supporting and critical views">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {supporting.content && (
                                <div className="bg-[#111827] border border-[#243044] border-l-4 border-l-[#2F80ED] rounded-xl p-6">
                                    <div className="text-[10px] uppercase tracking-wider text-[#56CCF2] mb-3">Supporting view</div>
                                    <p className="text-sm text-[#CBD5E1] leading-relaxed">{supporting.content}</p>
                                </div>
                            )}
                            {critical.content && (
                                <div className="bg-[#111827] border border-[#243044] border-l-4 border-l-[#94A3B8] rounded-xl p-6">
                                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-3">Critical view</div>
                                    <p className="text-sm text-[#CBD5E1] leading-relaxed">{critical.content}</p>
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                {/* 8. Connection Map */}
                {topic.related_topics && topic.related_topics.length > 0 && (
                    <Section id="connection-map" label="08 / Connection map" title="Connected topics">
                        <div className="flex flex-wrap gap-2">
                            {topic.related_topics.map((rt) => (
                                <Link
                                    key={rt}
                                    to={`/topic/${rt}`}
                                    className="px-4 py-2 rounded-full bg-[#111827] border border-[#243044] text-sm text-[#CBD5E1] hover:border-[#2F80ED] hover:text-white transition-colors"
                                >
                                    {rt}
                                </Link>
                            ))}
                        </div>
                    </Section>
                )}

                {/* Timeline */}
                {(topic.timeline?.length || tlGhosts.length) > 0 && (
                    <Section id="timeline" label="Timeline" title="Documented timeline">
                        <Timeline items={topic.timeline} />
                        {!isMember && tlGhosts.length > 0 && (
                            <div className="mt-6">
                                <div className="bg-[#0F1828] border border-dashed border-[#243044] rounded-xl px-5 py-5 space-y-3">
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                                        {tlGhosts.length} earlier event{tlGhosts.length === 1 ? "" : "s"} in archive
                                    </p>
                                    <div className="space-y-3">
                                        {tlGhosts.slice(0, 6).map((g, i) => (
                                            <GhostTimelineRow key={i} item={g} />
                                        ))}
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <ContinueInvestigationCTA
                                        compact
                                        testId="timeline-premium-cta"
                                        label="Full documented timeline"
                                        subline="All documented events — across every decade, every actor, every documented connection — are reserved for members."
                                    />
                                </div>
                            </div>
                        )}
                    </Section>
                )}

                {/* Entities */}
                {topic.entities && topic.entities.length > 0 && (
                    <Section id="entities" label="Key entities" title="People, companies & organisations">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {topic.entities.map((e, i) => (
                                <EntityCard key={e.id || i} entity={e} index={i} />
                            ))}
                        </div>
                        {!isMember && (
                            <p className="text-xs text-[#64748B] mt-4 max-w-2xl">
                                Names + roles are public. Member archive opens full entity profiles — bios, co-occurrences, source map, and timeline arc.
                            </p>
                        )}
                    </Section>
                )}

                {/* 9. Real-world Examples */}
                {examples.length > 0 ? (
                    <Section id="real-world-examples" label="09 / Real-world examples" title="Real-world examples">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {examples.map((ex, i) => (
                                <div key={i} className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                                    {ex.title && <p className="text-sm font-semibold text-white mb-2">{ex.title}</p>}
                                    {ex.content && <p className="text-sm text-[#94A3B8] leading-relaxed">{ex.content}</p>}
                                </div>
                            ))}
                        </div>
                    </Section>
                ) : (!isMember && counts.examples_total > 0 && (
                    <PremiumSection
                        testId="premium-real-world-examples"
                        label="09 / Real-world examples"
                        title="Real-world examples"
                        hint="Curated case studies where this system has played out in practice."
                        count={counts.examples_total}
                    />
                ))}

                {/* 10. What's Unclear */}
                {unknowns.length > 0 ? (
                    <Section id="unknowns" label="10 / What's unclear" title="What's unclear">
                        <ul className="space-y-3 max-w-3xl">
                            {unknowns.map((u, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <AlertCircle className="w-4 h-4 mt-0.5 text-[#94A3B8] shrink-0" />
                                    <span className="text-sm text-[#CBD5E1]">{u}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>
                ) : (!isMember && counts.unknowns_total > 0 && (
                    <PremiumSection
                        testId="premium-unknowns"
                        label="10 / What's unclear"
                        title="What's unclear"
                        hint="The honest gaps — what is documented, what is speculation, and what remains genuinely contested."
                        count={counts.unknowns_total}
                    />
                ))}

                {/* 11. Future Outlook */}
                {future.content ? (
                    <Section id="future" label="11 / Where this could go" title="Where this could go">
                        <div className="flex items-start gap-3 max-w-3xl">
                            <ArrowRight className="w-4 h-4 mt-1 text-[#56CCF2] shrink-0" />
                            <p>{future.content}</p>
                        </div>
                    </Section>
                ) : (!isMember && counts.unknowns_total > 0 && (
                    <PremiumSection
                        testId="premium-future"
                        label="11 / Where this could go"
                        title="Where this could go"
                        hint="The forward trajectory — projections grounded in the documented record, with explicit confidence labels."
                    />
                ))}

                {/* 12. Key Takeaways */}
                {takeaways.length > 0 ? (
                    <Section id="takeaways" label="12 / Key takeaways" title="Key takeaways">
                        <div className="bg-[#111827] border border-[#243044] rounded-xl p-6 max-w-3xl">
                            <ul className="space-y-3">
                                {takeaways.map((t, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#56CCF2] shrink-0" />
                                        <span className="text-sm text-[#CBD5E1] leading-relaxed">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Section>
                ) : (!isMember && counts.takeaways_total > 0 && (
                    <PremiumSection
                        testId="premium-takeaways"
                        label="12 / Key takeaways"
                        title="Key takeaways"
                        hint="The condensed conclusions — what the documented record actually supports."
                        count={counts.takeaways_total}
                    />
                ))}

                {/* Sources */}
                {((topic.sources?.length || 0) + srcGhosts.length) > 0 && (
                    <Section id="sources" label="Sources" title={`Sources (${(topic.sources?.length || 0) + srcGhosts.length})`}>
                        <ul className="space-y-3">
                            {(topic.sources || []).map((src, i) => (
                                <SourceItem key={src.id || i} source={src} index={i} />
                            ))}
                        </ul>
                        {!isMember && srcGhosts.length > 0 && (
                            <div className="mt-6">
                                <div className="space-y-3">
                                    {srcGhosts.slice(0, 6).map((g, i) => (
                                        <GhostSourceCard key={i} item={g} />
                                    ))}
                                </div>
                                <div className="mt-6">
                                    <ContinueInvestigationCTA
                                        compact
                                        testId="sources-premium-cta"
                                        label="Full source vault"
                                        subline="Every citation — peer-reviewed, regulatory, court filings, investigative reporting — open in the Source Vault for members. With verification date and topic cross-references."
                                    />
                                </div>
                            </div>
                        )}
                    </Section>
                )}

                {/* 13. Next Topics */}
                {next.length > 0 ? (
                    <Section id="next-topics" label="13 / Next topics" title="Continue exploring">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {next.map((slug2) => (
                                <Link
                                    key={slug2}
                                    to={`/topic/${slug2}`}
                                    className="px-5 py-4 rounded-xl bg-[#111827] border border-[#243044] hover:border-[#2F80ED] transition-colors text-sm text-[#CBD5E1] hover:text-white flex items-center justify-between"
                                >
                                    <span>{slug2}</span>
                                    <ArrowRight className="w-4 h-4 text-[#56CCF2]" />
                                </Link>
                            ))}
                        </div>
                    </Section>
                ) : (!isMember && counts.next_topics_total > 0 && (
                    <PremiumSection
                        testId="premium-next-topics"
                        label="13 / Next topics"
                        title="Continue exploring"
                        hint="Curated rabbit-hole continuations — the documented chains that lead from this investigation outward."
                        count={counts.next_topics_total}
                    />
                ))}

                {/* Build 4 — curated "Next investigation" exit cards.
                    Hydrated by the backend with reason chips + destination
                    metadata. Renders nothing when editorial hasn't curated. */}
                <NextInvestigations
                    fromSlug={topic.slug}
                    fromCluster={topic.next_topic_cluster || topic.category || null}
                    entries={topic.next_investigations || []}
                />

                {/* Report an issue */}
                <div className="mt-16 pt-10 border-t border-[#243044] text-center">
                    <a
                        data-testid="report-issue-link"
                        href={topic.issue_report_url?.startsWith("http") ? topic.issue_report_url : `https://thecontextproject.com${topic.issue_report_url || "/pages/report-an-issue"}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#56CCF2] underline underline-offset-4 decoration-[#243044] hover:decoration-[#56CCF2] transition-colors"
                    >
                        <Flag className="w-3.5 h-3.5" />
                        Report an issue with this topic
                    </a>
                    <p className="text-xs text-[#64748B] mt-3">Found incorrect information, broken links, or missing context? Let us know.</p>
                </div>

                {/* Wave 6 — invite contributions, makes archive feel alive */}
                <div className="mt-10">
                    <SourceLeadCTA slug={topic.slug} />
                </div>

                {/* Wave 3 — Subtle on-page attribution. Always visible so
                    screenshots organically carry brand back to TCP. */}
                <div className="mt-6">
                    <ShareAttribution slug={topic.slug} kind="topic" />
                </div>
                {topic?.id ? (
                    <div className="mt-8">
                        <RelatedVaccinesPanel topicId={topic.id} parentRef={`topic:${topic.slug}`} />
                    </div>
                ) : null}
            </div>

            <Footer />
        </div>
    );
}
